import React, { useEffect, useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import Toast from 'react-native-toast-message';
import { accessService } from '../../../services/central-api/access';
import DeleteModal from '../../../components/DeleteModal';
import { ClientError } from '../../../services/central-api/base-service';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { AccessParamList } from '../../../typings/Params';
import MyDataTable from '../../../components/MyDataTable';
import CustomButton from '../../../components/CustomButton';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import Filter from './Filters';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableContainer from '../../../components/DataTableContainer';
import DataTableHeader from '../../../components/DataTableHeader';
import DropdownButton from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';

export default function PermissionLevels() {
    const navigation = useNavigation<NavigationProp<AccessParamList>>();
    const route = useRoute<RouteProp<AccessParamList, 'PermissionLevels'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<AccessParamList['PermissionLevels']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [totalItems, setTotalItems] = useState(0);

    const [permissionLevels, setPermissionLevels] = useState<PermissionLevelData[]>([]);
    const [selectedPermissionLevel, setSelectedPermissionLevel] = useState<PermissionLevelData>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['accessPermissionLevels', filters],
        queryFn: () => accessService.getPermissionLevels(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setPermissionLevels(data.rows);
        setTotalItems(data.count);
    }, [data]);

    async function confirmDelete(permissionLevels: PermissionLevelData) {
        try {
            if (permissionLevels?.id) {
                await accessService.deletePermissionLevel(permissionLevels.id);
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });

                setDeleteModalVisible(false);
                refetch();
                setSelectedPermissionLevel(undefined);
            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));

            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
        }
    }

    return (
        <Container>
            <Header
                title={translate('permissionLevels')}
                actions={<CustomButton text={translate('register')} onPress={() => {
                    setSelectedPermissionLevel(undefined);
                    navigation.navigate('PermissionLevelForm');
                }} />}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredPermissionLevels')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter}
                        onChange={setTextFilter}
                        placeholder={translate('typeToSearch')}
                        onKeyPress={handleKeyDown}
                    />}
                    filter={<Filter setTextFilter={setTextFilter} textFilter={textFilter} filters={filters} setFilters={setFilters} />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[{
                            name: translate('name'),
                            selector: row => row.name,
                            grow: 2,
                            wrap: true
                        },
                        {
                            name: translate('actingBody'),
                            selector: row => row.actingBodyUnit?.actingBody?.name || '',
                            grow: 2,
                        },
                        {
                            name: translate('unit'),
                            selector: row => row.actingBodyUnit?.name || '',
                            grow: 2,
                        }, {
                            name: translate('userCount'),
                            selector: row => row.userCount || 0,
                            center: true
                        },
                        {
                            button: true,
                            cell: row => <DropdownButton
                                actions={[{
                                    content: translate('editInformation'),
                                    onPress: () => {
                                        setSelectedPermissionLevel(row);
                                        navigation.navigate('PermissionLevelForm', { id: row.id });
                                    }
                                }, {
                                    content: translate('delete'),
                                    onPress: () => {
                                        setSelectedPermissionLevel(row);
                                        setDeleteModalVisible(true);
                                    },
                                    variant: 'danger',
                                }]}
                            />
                        }]}
                        data={permissionLevels}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={totalItems}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>

            <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={String(selectedPermissionLevel?.name) || ''}
                onSubmit={async () => {
                    if (selectedPermissionLevel) {
                        await confirmDelete(selectedPermissionLevel);
                    }
                }}
                onCancel={() => {
                    setSelectedPermissionLevel(undefined);
                }}
            />
        </Container>
    );
}
